import { useQuery } from '@tanstack/react-query';
import { queryKeys } from './query-client';
import { useWorkspaces } from './workspaces';
import { fetchTasks, unsettledTasksRefetchInterval, type WebTask } from './tasks';
import { useAgentTaskDuplexer } from './use-agent-task-duplexer';
import { buildIslandQuickView, islandLead, type IslandLead, type IslandQuickView, type QuickViewTask } from './island-quickview';
import type { IslandColor } from './agent-task-buckets';

export type IslandQuickViewState = { status: 'loading' } | { status: 'ready'; data: IslandQuickView } | { status: 'error' };

/**
 * The island quick-view's data — the peek the popover (web) / sheet (native) render.
 *
 * It reads the **account-wide `/api/tasks` list**, on the SAME React Query key the Tasks screen renders
 * from (`queryKeys.tasks(undefined)`), so the bubble is a projection of that screen rather than a
 * parallel list. It used to fan out over `/api/workspace-tasks?workspaceId=` once per workspace, and that
 * quietly made it a DIFFERENT list: that route returns raw AgentTasks scoped to a workspace plus one
 * level of child projects, so the bubble could never show an inbound outreach row (they have no
 * workspace), nor a task whose `workspaceId` is empty or whose Space is missing from the roster. A row on
 * the screen that cannot appear in the bubble is the same class of bug as the two disagreeing about order.
 *
 * Reading one account-wide entry also costs FEWER requests than the fan-out it replaces — one call
 * instead of one per Space — but it is a heavier call, and the pill is mounted on every screen: this is
 * now the app's ambient task read. Hence `staleTime` + `refetchOnMount: false`; freshness comes from the
 * agent-task duplexer below, which is the signal that actually means something changed — under
 * `useTasks`'s own floor (`unsettledTasksRefetchInterval`), which this observer takes for the reason that
 * timer exists: it was added because a dropped duplexer frame left THE PILL claiming work that had
 * finished, and the pill is mounted everywhere while `/tasks` is one screen. A floor only the Tasks screen
 * carried would leave the most visible reader of this entry the stalest one. Unsettled-gated and
 * foreground-only there, so a finished list still costs nothing.
 *
 * ⚠️ One consequence worth naming: `/api/tasks` WRITES on the read path (`reconcileAbandoned` settles
 * tasks whose loop died), so that reconciliation now runs from every screen rather than only from
 * `/tasks`. It is idempotent and finds nothing on the second pass, but it belongs off a GET; moving it is
 * tracked separately rather than folded into this change.
 */
export function useIslandQuickView(): IslandQuickViewState {
  const workspacesState = useWorkspaces();
  const workspaces = workspacesState.status === 'ready' ? workspacesState.workspaces : [];

  const query = useQuery({
    queryKey: queryKeys.tasks(undefined),
    queryFn: () => fetchTasks(),
    staleTime: 60_000,
    refetchOnMount: false,
    refetchInterval: (data) => unsettledTasksRefetchInterval(data as WebTask[] | undefined),
    refetchIntervalInBackground: false,
  });

  // Freshness comes from here, not from mount. Measured consequence, named rather than smoothed over: on
  // `/tasks` BOTH this observer and `useTasks()`'s own listener fire for one duplexer event, and
  // `refetch()` defaults to `cancelRefetch: true` — so that screen issues two `/api/tasks` calls per event,
  // the first abandoned mid-flight. Passing `cancelRefetch: false` would collapse them, at the cost of
  // possibly serving a response that started BEFORE the change the event announced; that trade belongs with
  // the `reconcileAbandoned`-off-the-GET work, not here.
  useAgentTaskDuplexer(() => {
    void query.refetch();
  });

  // Data first: React Query keeps the last good list alongside an `error` status on a failed REFETCH, and
  // showing the rows we have beats blanking the bubble over a transient failure. Error only before the
  // first success; loading until then.
  if (!query.data) return query.status === 'error' ? { status: 'error' } : { status: 'loading' };

  const names = new Map(workspaces.map((workspace) => [workspace.id, workspace.name]));
  const tasks: QuickViewTask[] = query.data.map((task: WebTask) => ({
    id: task.id ?? '',
    title: (task.title ?? '').trim(),
    status: task.status ?? '',
    agentId: task.agentId,
    // Both empty on an outreach row (an Outreach belongs to no Space) — the row simply shows no Space.
    workspaceId: task.workspaceId ?? '',
    workspaceName: (task.workspaceId && names.get(task.workspaceId)) || '',
    summary: task.summary?.trim() || undefined,
    updatedDate: task.updatedDate,
    // Carried so the row can title and open itself the way `TaskCard` does — an outreach has no task
    // detail behind it, so a click must open the thread, not a modal with nothing to show.
    ...(task.rowKind === 'outreach' ? { isOutreach: true, senderName: task.senderName } : {}),
    ...(typeof task.runInFlight === 'boolean' ? { runInFlight: task.runInFlight } : {}),
  }));

  return { status: 'ready', data: buildIslandQuickView(tasks) };
}

/**
 * The pill's lead task for its current color — the line-2 source of the expanded blue/orange capsule.
 * Reads the SAME task list `useIslandQuickView` builds (one shared React Query cache, so no extra
 * fetch), and picks the newest task in the color's bucket from the FULL sorted set. `null` while the
 * list is still loading, for green, or when the color's bucket is empty — the pill then stays one line.
 */
export function useIslandLead(color: IslandColor): IslandLead | null {
  const state = useIslandQuickView();
  if (state.status !== 'ready') return null;
  return islandLead(state.data.sorted, color);
}
